import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import api from "constants/api";
import ProductCard from "components/ProductCard";
import SkewLoader from "react-spinners/SkewLoader";
import Heading from "components/Heading/Heading";

interface Category {
    _id: string;
    name: string;
    slug: string;
    special_price_product?: string;
}

interface Product {
    _id: string;
    special_product_available?: boolean;
    [key: string]: any;
}

const SpecialPriceProducts: FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState<Category | null>(null);
    const [products, setProducts] = useState<Product[]>([]);

    useEffect(() => {
        if (slug) {
            fetchCategoryAndProducts();
        }
    }, [slug]);

    const fetchCategoryAndProducts = async () => {
        setLoading(true);
        try {
            // First, get the category details from localStorage or API
            const categoryDataString = localStorage.getItem("categoryData");
            let categoryData = categoryDataString ? JSON.parse(categoryDataString) : [];

            // If no data in localStorage, fetch from API
            if (categoryData.length === 0) {
                const categoryUrl = process.env.REACT_APP_API_URL + api.frontCategory;
                const categoryRes = await axios.post(categoryUrl, {});

                if (categoryRes.data.statusCode === 200) {
                    categoryData = categoryRes.data.data.category;
                    localStorage.setItem("categoryData", JSON.stringify(categoryData));
                }
            }

            // Find the category by slug
            const foundCategory = categoryData.find(
                (cat: Category) => cat.slug === slug
            );

            if (foundCategory) {
                setCategory(foundCategory);

                // Fetch products for this category
                const productsUrl = process.env.REACT_APP_API_URL + api.frontProductGroup;
                const payload = {
                    category: foundCategory._id,
                    start: 0,
                    limit: 100
                };

                const productsRes = await axios.post(productsUrl, payload);

                if (productsRes.data.statusCode === 200 && productsRes.data.data?.result) {
                    const allProducts = productsRes.data.data.result;

                    // Filter only special products
                    const specialProducts = allProducts.filter(
                        (product: any) => product.special_product_available === true
                    );

                    setProducts(specialProducts);
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="nc-SpecialPriceProducts">
            <Helmet>
                <title>
                    {category ? `Special Price - ${category.name}` : "Special Price"} | Vijaydeep
                </title>
            </Helmet>

            {loading && (
                <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
                    <SkewLoader color="#2A3491" />
                </div>
            )}

            <div className="container py-16 lg:pb-28 lg:pt-20 space-y-16">
                {/* Breadcrumb */}
                {/* <div className="flex items-center gap-2 text-sm">
                    <Link to="/special-price" className="text-primary-6000 hover:underline">
                        Special Price
                    </Link>
                    <span>/</span>
                    <span className="text-neutral-500">{category?.name}</span>
                </div> */}

                {/* Products Section */}
                <div>
                    <Heading>
                        {category ? `Special Products – ${category.name}` : "Special Products"}
                    </Heading>

                    {products.length > 0 ? (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-10 mt-6">
                            {products.map((product: any) => (
                                <ProductCard data={product} key={product._id} isSpecialPrice={true} />
                            ))}
                        </div>
                    ) : (
                        !loading && (
                            <p className="text-neutral-500 mt-6">
                                No special products available in this category.
                            </p>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default SpecialPriceProducts;
